import LiquidText from './LiquidText';
import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import AppIcon from './AppIcon';
import DeleteIcon from './DeleteIcon';
import { createPortal } from 'react-dom';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';

let openDialogs = 0;
let restorePage = () => {};


export function OrcamentoIcon({ name, fallback }) {
    const { theme } = useMaquetesTheme();
    if (name === 'trash') return <DeleteIcon />;
    if (theme !== 'liquido') return <>{fallback}</>;
    return <AppIcon name={name} className="orc-theme-icon" />;
}

// Portals keep forms clear of the page's scroll and glass stacking contexts.
export function OrcamentoDialog({ children, onClose, label = 'Formulário de orçamento', ...props }) {
    const { theme } = useMaquetesTheme();
    const overlay = useRef(null);
    const closeRef = useRef(onClose);
    const titleId = useId();
    closeRef.current = onClose;
    useEffect(() => {
        const previous = document.activeElement;
        if (openDialogs++ === 0) {
            const main = document.querySelector('.main-content');
            const app = document.querySelector('.app-layout');
            const oldOverflow = main?.style.overflow;
            const oldInert = app?.inert;
            if (main) main.style.overflow = 'hidden';
            if (app) app.inert = true;
            restorePage = () => { if (main) main.style.overflow = oldOverflow; if (app) app.inert = oldInert; };
        }
        const node = overlay.current;
        const card = node.firstElementChild;
        const title = card.querySelector('h2, h3, .modal-ef-num, .orc-dialog-title, .modal-ef-header > div');
        card.setAttribute('role', 'dialog');
        card.setAttribute('aria-modal', 'true');
        card.tabIndex = -1;
        if (title) { title.id = titleId; card.setAttribute('aria-labelledby', titleId); }
        else card.setAttribute('aria-label', label);
        // Associate the existing compact field captions with their controls.
        card.querySelectorAll('.modal-field, .contacto-field, .form-group, .planeamento-envio-grid > div, .worker-form-field, .gf-field, .mobile-form-group').forEach((field, i) => {
            const caption = field.querySelector('label');
            const control = field.querySelector('input, select, textarea');
            if (caption && control && !caption.htmlFor) { if (!control.id) control.id = `${titleId}-field-${i}`; caption.htmlFor = control.id; }
        });
        card.querySelectorAll('.admin-field-label, [data-liquid-label]').forEach((caption, i) => {
            const control = caption.parentElement.querySelector('input, select, textarea');
            if (control) { if (!control.id) control.id = `${titleId}-admin-${i}`; caption.htmlFor = control.id; }
        });
        card.focus({ preventScroll: true });
        const keydown = e => {
            const dialogs = document.querySelectorAll('.orc-dialog-overlay');
            if (dialogs[dialogs.length - 1] !== node) return;
            if (e.key === 'Escape' && closeRef.current) { e.preventDefault(); e.stopPropagation(); closeRef.current(); }
            if (e.key !== 'Tab') return;
            const elements = [...card.querySelectorAll('button, input, select, textarea, a[href], [tabindex="0"]')].filter(el => !el.disabled && el.getClientRects().length);
            const first = elements[0], last = elements[elements.length - 1];
            if (!first) { e.preventDefault(); card.focus(); }
            else if (e.shiftKey && (document.activeElement === first || document.activeElement === card)) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && (document.activeElement === last || document.activeElement === card)) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', keydown, true);
        return () => { document.removeEventListener('keydown', keydown, true); if (--openDialogs === 0) restorePage(); if (previous?.isConnected) previous.focus({ preventScroll: true }); };
    }, [label, titleId]);
    return createPortal(<div {...props} ref={overlay} className="modal-overlay orc-dialog-overlay" data-orc-theme={theme} onClick={e => { if (e.target === e.currentTarget) closeRef.current?.(); }}>{children}</div>, document.body);
}

const FeedbackContext = createContext(null);
export const useOrcamentoFeedback = () => useContext(FeedbackContext);
export function OrcamentoFeedbackProvider({ children }) {
    const { theme } = useMaquetesTheme();
    const themeRef = useRef(theme);
    themeRef.current = theme;
    const [queue, setQueue] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const pending = useRef(new Set());
    useEffect(() => () => { pending.current.forEach(resolve => resolve(false)); pending.current.clear(); }, []);
    const ask = useCallback((message, confirmation, input = false, options = {}) => {
        if (themeRef.current !== 'liquido') return Promise.resolve(input ? window.prompt(message, options.defaultValue || '') : confirmation ? window.confirm(message) : window.alert(message));
        return new Promise(resolve => { pending.current.add(resolve); setQueue(items => [...items, { message, confirmation, input, options, resolve }]); });
    }, []);
    const confirm = useCallback(message => ask(message, true), [ask]);
    const notify = useCallback((message, options = {}) => ask(message, false, false, options), [ask]);
    const prompt = useCallback((message, options) => ask(message, false, true, options), [ask]);
    const current = queue[0];
    useEffect(() => { setInputValue(current?.options?.defaultValue || ''); }, [current]);
    const settle = value => { current.resolve(current.input ? (value === false ? null : inputValue) : value); setInputValue(''); pending.current.delete(current.resolve); setQueue(items => items.slice(1)); };
    const destructive = current && /eliminar|remover|apagar|reverter|limpar/i.test(current.message);
    return <FeedbackContext.Provider value={{ confirm, notify, prompt }}>{children}{current &&
        <OrcamentoDialog onClose={() => settle(false)} label={current.input ? (current.options.title || 'Destinatários') : current.confirmation ? 'Confirmar ação' : 'Informação'}>
            <div className="modal-ef orc-feedback-dialog">
                <div className="modal-ef-header"><h3 className="orc-dialog-title">{current.input ? (current.options.title || 'Destinatários') : current.confirmation ? 'Confirmar ação' : 'Informação'}</h3><button className="modal-close" aria-label="Fechar" onClick={() => settle(false)}><OrcamentoIcon name="close" fallback="✕" /></button></div>
                <div className="modal-form-body"><p className="orc-feedback-message">{current.options.icon && <span className="orc-feedback-message-icon"><AppIcon name={current.options.icon} size={18} /></span>}<LiquidText>{current.message}</LiquidText></p>{current.input && <div className="modal-field" style={{ marginBottom: 20 }}><label htmlFor="feedback-destinatarios">{current.options.label || 'Email(s)'}</label><input id="feedback-destinatarios" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && inputValue.trim()) settle(true); }} autoComplete="off" /></div>}</div>
                <div className="modal-form-actions">{(current.confirmation || current.input) && <button className="btn-modal-cancel" onClick={() => settle(false)}>Cancelar</button>}<button className={destructive && current.confirmation ? 'btn-modal-danger' : 'btn-modal-save'} disabled={current.input && !inputValue.trim()} onClick={() => settle(true)}>{current.input ? 'Continuar' : current.confirmation ? 'Confirmar' : 'Fechar'}</button></div>
            </div>
        </OrcamentoDialog>
    }</FeedbackContext.Provider>;
}
